import { Logger } from '@egw/logger'
import type {
  RSAnalytics,
  RSALogger,
  RSAEvent,
  RSACustomIntegration,
  CustomDestinationConfig,
} from '@rudderstack/analytics-js'

const log = new Logger('Analytics Service - FullStory Custom Integration')

// Window type declaration (referenced in @egw/screen-recording)
declare global {
  interface Window {
    evergreenFullStory?: {
      initializeFullStorySDK: (_config: { orgId: string; enableLogging?: boolean }) => void
      identifyUser: (_userId: string, _traits?: Record<string, unknown>) => void
      trackEvent: (_eventName: string, _properties?: Record<string, unknown>) => void
      isReady: () => boolean
    }
  }
}

/**
 * PII fields that should be stripped from user traits and event properties
 * before sending to FullStory
 */
const PII_FIELDS = [
  // Personal identifiers
  'email',
  'phone',
  'phoneNumber',
  'phoneCountryCode',
  'firstName',
  'lastName',
  'name',
  'fullName',

  // Address fields
  'address',
  'addressLine1',
  'addressLine2',
  'street',
  'city',
  'state',
  'zipCode',
  'postalCode',
  'country',

  // Financial information
  'ssn',
  'ssnLastFour',
  'accountNumber',
  'routingNumber',
  'cardNumber',

  // Sensitive dates
  'birthDate',
  'dateOfBirth',
  'dob',

  // Employment
  'employer',
  'occupation',

  // Political/regulatory
  'affiliatedPoliticalOrganizations',
  'politicallyExposedImmediateFamilyMembers',
] as const

/**
 * Strip PII fields from an object recursively
 * Returns a new object with PII fields removed
 */
function stripPII<T extends Record<string, unknown>>(data: T): Partial<T> {
  if (!data || typeof data !== 'object') {
    return data
  }

  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    // Check if field is PII (case-insensitive)
    const isPII = PII_FIELDS.some((piiField) => key.toLowerCase() === piiField.toLowerCase())

    if (isPII) {
      // Skip PII fields entirely
      log.debug(`Stripped PII field: ${key}`)
      continue
    }

    // Recursively strip PII from nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = stripPII(value as Record<string, unknown>)
    }
    // Handle arrays of objects
    else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === 'object' && item !== null ? stripPII(item as Record<string, unknown>) : item,
      )
    }
    // Keep non-PII primitive values
    else {
      sanitized[key] = value
    }
  }

  return sanitized as Partial<T>
}

/**
 * FullStory Custom Integration for RudderStack v3
 *
 * This integration acts as a bridge between RudderStack and the FullStory Service.
 * It automatically strips PII from all user traits and event properties before
 * sending to FullStory to ensure compliance with privacy regulations.
 *
 * Architecture:
 * RudderStack → FullStory Custom Integration → window.evergreenFullStory → FullStory SDK
 *
 * PII Protection:
 * - Strips PII fields from identify() traits
 * - Strips PII fields from track() properties
 * - Logs stripped fields for audit purposes
 */
export const createFullStoryCustomIntegration = (config: {
  orgId: string
  enableLogging?: boolean
}): RSACustomIntegration => {
  log.info('Creating FullStory custom integration', { orgId: config.orgId })

  return {
    /**
     * Initialize FullStory SDK via window.evergreenFullStory
     * Called by RudderStack during destination configuration
     *
     * @param destinationConfig - The custom destination configuration from RudderStack dashboard
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     */
    init: (destinationConfig: CustomDestinationConfig, analytics: RSAnalytics, logger: RSALogger): void => {
      if (typeof window === 'undefined') {
        log.warn('Window object not available - skipping FullStory initialization')
        return
      }

      if (!window.evergreenFullStory) {
        log.warn('window.evergreenFullStory not available - FullStory Service may not be loaded yet')
        return
      }

      try {
        window.evergreenFullStory.initializeFullStorySDK({
          orgId: config.orgId,
          enableLogging: config.enableLogging ?? false,
        })

        log.info('FullStory SDK initialized successfully via custom integration')
      } catch (error) {
        log.error('Failed to initialize FullStory SDK via custom integration', error)
      }
    },

    /**
     * Check if the FullStory integration is ready to process events
     * REQUIRED method for RSACustomIntegration
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @returns boolean indicating if FullStory is ready
     */
    isReady: (analytics: RSAnalytics, logger: RSALogger): boolean => {
      const ready =
        typeof window !== 'undefined' &&
        Boolean(window.evergreenFullStory) &&
        Boolean(window.evergreenFullStory?.isReady())

      log.debug('FullStory custom integration isReady() check', {
        ready,
        hasWindow: typeof window !== 'undefined',
        hasEvergreenFullStory: typeof window !== 'undefined' && Boolean(window.evergreenFullStory),
        hasAnalytics: Boolean(analytics),
        hasLogger: Boolean(logger),
      })

      return ready
    },

    /**
     * Process identify events with PII stripping
     * Maps RudderStack identify() calls to window.evergreenFullStory.identifyUser()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The identify event payload
     */
    identify: (analytics: RSAnalytics, logger: RSALogger, event: RSAEvent): void => {
      if (!window.evergreenFullStory) {
        log.warn('window.evergreenFullStory not available - cannot identify user')
        return
      }

      try {
        const userId = event.message.userId
        const rawTraits = event.message.context.traits as Record<string, unknown> | undefined

        if (userId) {
          // Strip PII from traits before sending to FullStory
          const sanitizedTraits = rawTraits ? stripPII(rawTraits) : undefined

          window.evergreenFullStory.identifyUser(userId, sanitizedTraits)

          log.info('User identified successfully in FullStory (PII stripped)', {
            userId,
            fieldsStripped: rawTraits ? Object.keys(rawTraits).length - Object.keys(sanitizedTraits || {}).length : 0,
          })
        } else {
          log.warn('No userId provided in identify event')
        }
      } catch (error) {
        log.error('Failed to identify user in FullStory', error)
      }
    },

    /**
     * Process track events with PII stripping
     * Maps RudderStack track() calls to window.evergreenFullStory.trackEvent()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The track event payload
     */
    track: (analytics: RSAnalytics, logger: RSALogger, event: RSAEvent): void => {
      if (!window.evergreenFullStory) {
        log.warn('window.evergreenFullStory not available - cannot track event')
        return
      }

      try {
        const eventName = event.message.event
        const rawProperties = event.message.properties as Record<string, unknown> | undefined

        if (eventName) {
          // Strip PII from event properties before sending to FullStory
          const sanitizedProperties = rawProperties ? stripPII(rawProperties) : undefined

          window.evergreenFullStory.trackEvent(eventName, sanitizedProperties)

          log.info('Event tracked successfully in FullStory (PII stripped)', {
            eventName,
            fieldsStripped: rawProperties
              ? Object.keys(rawProperties).length - Object.keys(sanitizedProperties || {}).length
              : 0,
          })
        } else {
          log.warn('No event name provided in track event')
        }
      } catch (error) {
        log.error('Failed to track event in FullStory', error)
      }
    },

    /**
     * Process page events
     * Maps RudderStack page() calls to window.evergreenFullStory.trackEvent()
     *
     * @param analytics - RudderStack analytics instance
     * @param logger - RudderStack logger instance
     * @param event - The page event payload
     */
    page: (analytics: RSAnalytics, logger: RSALogger, event: RSAEvent): void => {
      if (!window.evergreenFullStory) {
        log.warn('window.evergreenFullStory not available - cannot track page view')
        return
      }

      try {
        const eventName = (event as { message: { name: string; properties: Record<string, unknown> } }).message.name
        const rawProperties = event.message.properties as Record<string, unknown> | undefined

        // Strip PII from page properties
        const sanitizedProperties = rawProperties ? stripPII(rawProperties) : undefined

        window.evergreenFullStory.trackEvent(eventName, sanitizedProperties)

        log.info('Page view tracked successfully in FullStory', { eventName })
      } catch (error) {
        log.error('Failed to track page view in FullStory', error)
      }
    },
  }
}

// Export for testing
export { stripPII, PII_FIELDS }
